import {
  ModalRoot,
  ModalOverlay,
  ModalContentContainer,
  ModalHeader,
  ModalHeaderIcon,
  CloseButton,
  ClearButtonSymbol,
  ModalBody,
  GlossaryModalBody,
  ModalBodyContainer,
} from "./ModalStyles";
import { uiIcons } from "../../assets/icons";
import styled from "@emotion/styled";
import {
  borderRadius,
  shadow,
  spacing,
  fontWeight,
  dataViz,
  surface,
  text,
} from "@atlas-int/tokens";

import { navigationBarElementId } from "../navigationBar/NavigationBar";

export enum ModalType {
  DataNotes = "Data Notes",
  DataIssues = "Data Issues",
  Glossary = "Glossary",
  SubregionMap = "Subregion Map",
  Generic = "Generic",
  ProductOverlay = "ProductOverlay",
}

// Add a styled component that matches the Settings panel header style
const ProductOverlayHeader = styled.div`
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: ${text.onDark};
  font-weight: ${fontWeight.normal};
  padding: ${spacing[4]} 0px;
  text-align: center;
`;

// Add a custom modal header style for product overlay
const ProductOverlayModalHeader = styled(ModalHeader)`
  background-color: ${dataViz.primaryAction};
  border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
  padding: 0px ${spacing[10]};
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  border: none;
`;

// Add custom modal content container for product overlay
const ProductOverlayContentContainer = styled(ModalContentContainer)`
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  background-color: ${surface.raised};
  border: none;
  box-shadow: ${shadow.sm};
`;

interface ModalBaseProps {
  modalType: ModalType;
  toggleModal: () => void;
  children: React.ReactNode;
}

const ModalBase = ({ modalType, toggleModal, children }: ModalBaseProps) => {
  let modalPaddingLeft: string;
  let navigationBarElement = document.querySelector(
    `#${navigationBarElementId}`,
  );
  if (navigationBarElement) {
    let { width } = navigationBarElement.getBoundingClientRect();
    modalPaddingLeft = `${width}px`;
  } else {
    modalPaddingLeft = "0px";
  }

  let modalHeader: string;
  let modalBodyContents: React.ReactNode;
  let modalHeaderIcon: string | undefined;
  let overrideStyle: React.CSSProperties = { marginLeft: modalPaddingLeft };
  let customModalHeader = false;

  if (modalType === ModalType.DataNotes) {
    modalHeader = "Data Notes";
    modalBodyContents = <ModalBody>{children}</ModalBody>;
    modalHeaderIcon = uiIcons.datanotesOff;
  } else if (modalType === ModalType.DataIssues) {
    modalHeader = "Data Issues";
    modalBodyContents = <ModalBody>{children}</ModalBody>;
    modalHeaderIcon = uiIcons.datanotesOn;
  } else if (modalType === ModalType.Glossary) {
    modalHeader = "Glossary";
    modalBodyContents = <GlossaryModalBody>{children}</GlossaryModalBody>;
    modalHeaderIcon = uiIcons.glossary;
  } else if (modalType === ModalType.SubregionMap) {
    modalHeader = "Subregion Map";
    modalBodyContents = <ModalBody>{children}</ModalBody>;
    modalHeaderIcon = uiIcons.subregionMap;
    overrideStyle = {
      ...overrideStyle,
      width: "70%",
    };
  } else if (modalType === ModalType.ProductOverlay) {
    modalHeader = "Most Related Nearby Products";
    modalHeaderIcon = undefined;
    modalBodyContents = <ModalBody>{children}</ModalBody>;
    customModalHeader = true;
    overrideStyle = {
      ...overrideStyle,
      width: "85%",
    };
  } else {
    modalHeader = "";
    modalBodyContents = <ModalBody>{children}</ModalBody>;
    modalHeaderIcon = undefined;
  }

  return (
    <ModalRoot>
      <ModalOverlay onClick={() => toggleModal()}>
        {customModalHeader ? (
          <ProductOverlayContentContainer
            style={overrideStyle}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <ProductOverlayModalHeader>
              <ProductOverlayHeader>{modalHeader}</ProductOverlayHeader>
              <CloseButton
                onClick={() => toggleModal()}
                style={{ color: text.onDark }}
              >
                {ClearButtonSymbol}
              </CloseButton>
            </ProductOverlayModalHeader>
            <ModalBodyContainer>{modalBodyContents}</ModalBodyContainer>
          </ProductOverlayContentContainer>
        ) : (
          <ModalContentContainer
            style={overrideStyle}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <ModalHeader>
              {modalHeaderIcon ? (
                <ModalHeaderIcon
                  style={{
                    maskImage: `url(${modalHeaderIcon}`,
                    WebkitMaskImage: `url(${modalHeaderIcon}`,
                  }}
                />
              ) : null}
              <h1>{modalHeader}</h1>
              <CloseButton onClick={() => toggleModal()}>
                {ClearButtonSymbol}
              </CloseButton>
            </ModalHeader>
            <ModalBodyContainer>{modalBodyContents}</ModalBodyContainer>
          </ModalContentContainer>
        )}
      </ModalOverlay>
    </ModalRoot>
  );
};

export default ModalBase;
